/**
 * Environment Configuration Model
 * @Author "Andres Escobar Ciceri"
 */

import { MenuMode } from '@kanzi-apes/kanzi-diamond-ui';
import { DashboardItemModel } from '../interfaces';

export interface KanziEnvironmentModel {
  production: boolean;
  kongApi: string;
  kongTenantApi?: string;
  wmsKongApi?: string;
  kongTrackingFile: string;
  apesPdfCreatorApi?: string;
  masterFileUrl?: string;
  templatePdf?: string;
  flightReportApiUrl?: string;
  tokenFlightReport?: string;
  bucket_image_storage?: string;
  nodeRedApi?: string;
  sentryApi?: string;
  socketIOServer?: string;
  noderedCustomer?: string;
  qsApiUrl?: string;
  qsApiUrlCommon?: string;
  qsCustomer?: string;
  qsApiUrlAvianca?: string;
  isQuantity?: boolean;
  selectedLocation?: boolean;
  filterByLocation?: boolean;
  filterByEntryLocation?: boolean;
  helpdeskUrl?: string;
  gmtZone?: string;
  tenantId?: string;
  useEanAsId?: boolean; // Controls if EAN code should be used as product ID
  formats: {
    date: string;
  };
  assets: {
    logo: string;
    background: string;
    root: string;
    rootClient: string;
    paths: {
      css: string;
      logos: string;
      backgrounds: string;
      js: string;
    };
  };
  configUI?: {
    mainMenuStyle: MenuMode;
    scale: number;
  };
  apiModules: {
    auth: string;
    authToken: string;
    config: string;
    inventory?: string;
    operations?: string;
    rfid?: string;
    audits?: string;
    logistics?: string;
    customers?: string;
    integrations?: string;
    maintenance?: string;
    stock?: string;
    invoicing?: string;
    airCargo?: string;
  };
  apesBoxValidatorPdfTemplate?: string;
  pdfTemplatePath?: string;
  modules: {
    enabledTypeItems?: boolean;
    loadMasterFileLocation?: boolean;
    customerType: string;
    dashboards?: boolean;
    isEnableButtonExternalId?: boolean;
    isFilterWerehouse?: boolean;
    dashboardsList?: DashboardItemModel[];
    contacts?: boolean;
    editContacts?: boolean;
    discountsContacts?: boolean;
    disabledCreateContacts?: boolean;
    contactsParams?: boolean;
    automationId?: boolean;
    automaticPrinting?: boolean;
    useEanAsId?: boolean;
    products?: {
      parent: boolean;
      products: boolean;
      productCode?: boolean;
      assets: boolean;
      spareParts?: boolean;
      variableInputs?: boolean;
      maintenanceTrace: boolean;
      enableItemActivation?: boolean;
      unsubscribeProduct?: boolean;
      deleteProduct?: boolean;
      aditionalParamsInactiveTags?: boolean;
      inactiveTagsAccions?: boolean;
      activeComments?: boolean;
      filterTypeSku?: boolean;
      setProperties?: boolean;
      setImagesProduct?: boolean;
      loadMasterFile?: boolean;
      isDisabledCreateProduct?: boolean;
      editProducts?: boolean;
      propsRequired?: boolean;
      clientEan13?: boolean;
      clientUpc12?: boolean;
      loadProductGroups?: boolean;
    };
    airCargo?: {
      parent: boolean;
      list: boolean;
      receivePieces: boolean;
    };
    purchaseOrders?: {
      parent: boolean;
      loadMasterFile?: boolean;
    };
    inventories?: {
      parent: boolean;
      products: boolean;
      assets: boolean;
      booking: boolean;
      bookingCreate: boolean;
      bookingList: boolean;
      hideColumnsSummary?: boolean;
    };
    sales?: {
      parent?: boolean;
      layoutCreator?: boolean;
      disabledSalesReports?: boolean;
    };
    moves?: {
      parent: boolean;
      dashboards?: boolean;
      sales?: boolean;
      price?: boolean;
      moves?: boolean;
      moveExternalId?: boolean;
      canClose?: boolean;
    };
    production?: {
      parent: boolean;
      productionOrder: boolean;
      productionOrderEdit?: boolean;
      productionDelete?: boolean;
      productionLocationType: string;
      purchaseOrder: boolean;
      purchaseLocationType: string;
      printOrder: boolean;
      filterOrder: boolean;
      external_id: boolean;
      purchaseOrderDelete?: boolean;
      purchaseOrderEdit?: boolean;
      purchaseOrderEditAddLines?: boolean;
      productionPositioning?: boolean;
      purchasePositioning?: boolean;
      productionOrderProperties?: boolean;
      enabledInventoryProps?: boolean;
      purchaseOrderProperties?: boolean;
      productionOrderIdHide?: boolean;
      hideTagTemplate?: boolean;
      skuIdLines?: boolean;
      useSkuId?: boolean;
      loadMasterFile?: boolean;
      by_ean: boolean;
    };
    printOrders?: {
      generatePrintFile: boolean;
      printAgent?: boolean;
      printAgentUrl?: string;
      printAgentSocketUrl?: string;
      defaultPrinterIp?: string;
    };
    operations?: {
      parent: boolean;
      storeOrder: boolean;
      storeOrderTypes?: boolean;
      distributionOrder: boolean;
      workOrder: {
        parent: boolean;
        reasignCreateNewOrder: boolean;
        loadPackingsFile?: boolean;
      };
      packings: boolean;
      dispatchPacks: boolean;
      budgets?: boolean;
      booking?: boolean;
      validationClient?: boolean;
      woAssignsLeader?: boolean;
      /**
       * When true, the store order detail view queries client service discount
       * rates (invoicing/service-discount-rates/). Only enabled for the
       * kanzi-apes-maintenance app.
       */
      serviceDiscountRates?: boolean;
    };
    storeOrders?: {
      parent: boolean;
      loadMasterFile?: boolean;
    };
    audits?: {
      parent: boolean;
      calendar?: boolean;
      products: boolean;
      assets: boolean;
      classic?: boolean;
      virtualStore?: boolean;
      typeAuditProps?: 'REQUIRED' | 'OPTIONAL' | 'DEFAULT' | 'MULTIPLE';
      summaryDetail?: boolean;
      lossControlOption?: boolean;
      auditByProducts?: boolean;
    };
    logistics?: {
      parent: boolean;
      shippingOrders?: boolean;
      shippingReturns?: boolean;
      trackOrderCreate: boolean;
      trackings: boolean;
      printers: boolean;
      pickingFile?: boolean;
      hideStatusTracking?: boolean;
      sendSkuId?: boolean;
      formStatic?: boolean;
      hideAddressType?: boolean;
      shippingVerified?: boolean;
      onlySkuId?: boolean;
    };
    logisticPacking?: {
      parent: boolean;
      list: boolean;
      listStates: boolean;
      create: boolean;
      filterUser?: boolean;
      initialStateForValidation?: string;
      stateToCreateDispatch?: string;
      bulkCreatePackings?: boolean;
      bulkPrintAgent?: boolean;
      printAgentSocketUrl?: string;
    };
    maintenance?: {
      hasDiagnostics?: boolean;
      budgets?: ConditionalBudgets;
      module_name?: string;
      urlTemplatePdfOT?: string;
      parent: boolean;
      scheduler: boolean;
      orders: boolean;
      // workOrders?: { budget?: boolean };
      checklistSheetLlines?: boolean;
      byGroups?: boolean;
      pendingAssets?: boolean;
      disabledApproveOT?: boolean;
      byTypes?: boolean;
      pdfGenerator?: {
        revisionLine: boolean;
        revisionLineTemplate: string;
        revisionMarginsTemplate: number[];
      };
      pdfGeneratorAdditional?: {
        revisionLine: boolean;
        revisionLineTemplate: string;
        revisionMarginsTemplate: number[];
        pdfGeneratorName?: string;
        locationName?: string;
      };

      otUndiagnosed?: {
        visible: boolean;
        hasDescription: boolean;
        disabledSpareParts?: boolean;
        isMaintenanceEndpoint: boolean;
        urlTemplatePDFDiagnosed?: string;
      };
    };
    nodes?: {
      nodeTypes: { type: string; name: string }[];
    };
    alerts?: boolean;
    shipments?: {
      parent: boolean;
      list?: boolean;
      packages?: boolean;
    };
  };
  configModules?: {
    locations?: boolean;
    iconLocation?: boolean;
    customers?: boolean;
    logistics?: boolean;
    users?: boolean;
    maintenances?: boolean;
  };
  viewRigthMenu?: boolean;
}

export interface EnvironmentModel {
  production: boolean;
  kongApi: string;
  sentryApi: string;
  kongTrackingFile: string;
  formats: {
    date: string;
  };
  apiModules: {
    assets: string;
    auth: string;
    config: string;
    inventory: string;
    operations: string;
    rfid: string;
    audits: string;
    logistics: string;
    customers: string;
    integrations?: string;
    maintenance?: string;
    stock?: string;
  };
  assets: {
    logo: string;
    background: string;
    root: string;
    rootClient: string;
    paths: {
      css: string;
      logos: string;
      backgrounds: string;
      js: string;
    };
  };
  modules: {
    customerType?: string;
    dashboards: {
      parent: boolean;
      moves: boolean;
      purchase: boolean;
      inventory: boolean;
      users: boolean;
      inventoryBar: boolean;
      entries: boolean;
      sells?: boolean;
    };
    contacts: boolean;
    products: {
      parent: boolean;
      products: boolean;
      assets: boolean;
      maintenanceTrace: boolean;
    };
    inventories: {
      parent: boolean;
      products: boolean;
      assets: boolean;
      booking: boolean;
      bookingCreate: boolean;
      bookingList: boolean;
    };
    moves: boolean;
    production: {
      parent: boolean;
      productionOrder: boolean;
      purchaseOrder: boolean;
      printOrder: boolean;
      filterOrder: boolean;
      external_id: boolean;
    };
    operations: {
      parent: boolean;
      storeOrder: boolean;
      distributionOrder: boolean;
      workOrder: boolean;
      packings: boolean;
      dispatchPacks: boolean;
      external_id_config: {
        filed?: string;
        subfield?: string;
      };
    };
    audits: {
      parent: boolean;
      calendar?: boolean;
      products: boolean;
      assets: boolean;
    };
    hardware: boolean;
    logistics: {
      parent: boolean;
      shippingOrders?: boolean;
      trackOrderCreate: boolean;
      trackings: boolean;
      printers: boolean;
    };
    logisticPacking: {
      parent: boolean;
      list: boolean;
      listStates: boolean;
      create: boolean;
      bulkPrintAgent?: boolean;
      printAgentSocketUrl?: string;
    };
    maintenance: {
      parent: boolean;
      scheduler: boolean;
      orders: boolean;
    };
    shared: {
      parent: boolean;
    };
    alerts: boolean;
  };
  configModules: {
    locations: boolean;
    customers: boolean;
    logistics: boolean;
    users: boolean;
  };
  views: {
    packs_list_view: {
      visible: boolean;
      columns_view: {
        created_column: boolean;
        verified_column: boolean;
        dispatched_column: boolean;
      };
      table_view: any | null;
      dispatch_form: {
        visible: boolean;
        form: {
          location: {
            type: string;
          };
          packs_state_filter: string;
        };
        table: {
          lazy: boolean;
          showHeaderCheckbox: boolean;
        };
      };
    };
    inventory_summary_list_view: {
      visible: boolean;
      type_view: string;
      tabs_info: {
        count: number;
        tabs: {
          name: string;
          visible: boolean;
        }[];
      };
    };
    production_order_view: {
      cell_provider_select: boolean;
      print_template_select: boolean;
    };
    viewSellerOption: boolean;
  };
  viewRigthMenu: boolean;
}

export type ConditionalBudgets = 'OPTIONAL' | 'REQUIRED' | 'NONE';

export interface KanziWMSEnvironmentModel {
  link_desk_help?: string;
  environment?: string;
  template_optimal_route?: string;
  template_dispatch_orders?: string;
  template_purchase_orders?: string;
  template_return_orders?: string;
  template_transfer_orders?: string;
  template_products?: string;
  template_locations?: string;
  filter_warehouses_user?: boolean;
  bucket_image_storage?: string;
  production: boolean;
  printOrders?: {
    generatePrintFile?: boolean;
    printAgent?: boolean;
    printAgentUrl?: string;
    printAgentSocketUrl?: string;
    defaultPrinterIp?: string;
  };
  QS_INVENTORY_DASHBOARD_NAME?: string;
  cognitoConfig?: {
    cognitoUserPoolId: string;
    cognitoAppClientId: string;
    cognitoRegion: string;
    cognitoIdentityPoolId: string;
  };
  qsApiUrlCommon?: string;
  tenantId?: string;
  dataloaderHide: boolean;
  apiRestModules: {
    kongWMSAPI?: {
      locations: {
        apiURL: string;
        apiPath: string;
      };
      locationsV3: {
        apiURL: string;
        apiPath: string;
      };
      locationTypes: {
        apiURL: string;
        apiPath: string;
      };
      userConfigs: {
        apiURL: string;
        apiPath: string;
      };
      thirdParties: {
        apiURL: string;
        apiPath: string;
      };
      products: {
        apiURL: string;
        apiPath: string;
      };
      stockProduct: {
        apiURL: string;
        apiPath: string;
      };
      stockProductV3: {
        apiURL: string;
        apiPath: string;
      };
      users: {
        apiURL: string;
        apiPath: string;
      };
      orders: {
        apiURL: string;
        apiPath: string;
      };
      enlistmentOrders: {
        apiURL: string;
        apiPath: string;
      };
      deliveryOrders: {
        apiURL: string;
        apiPath: string;
      };
      purchaseOrders: {
        apiURL: string;
        apiPath: string;
      };
      purchaseOrderLines: {
        apiURL: string;
        apiPath: string;
      };
      receiptsOrders: {
        apiURL: string;
        apiPath: string;
      };
      returnsOrders: {
        apiURL: string;
        apiPath: string;
      };
      dataLoader: {
        apiURL: string;
        apiPath: string;
      };
      user_roles: {
        apiURL: string;
        apiPath: string;
      };
      user_creation: {
        apiURL: string;
        apiPath: string;
      };
      userConfigUpdate: {
        apiURL: string;
        apiPath: string;
      };
      inventoryCounts: {
        apiURL: string;
        apiPath: string;
      };
      types: {
        apiURL: string;
        apiPath: string;
      };
      locationPriority: {
        apiURL: string;
        apiPath: string;
      };
      reports: {
        apiURL: string;
        apiPath: string;
      };
      containers: {
        apiURL: string;
        apiPath: string;
      };
      productsV3?: {
        apiURL: string;
        apiPath: string;
      };
      webhooks?: {
        apiURL: string;
        apiPath: string;
      };
      replenishmentConfigs?: {
        apiURL: string;
        apiPath: string;
      };
      replenishmentTasks?: {
        apiURL: string;
        apiPath: string;
      };
    };
    kongWMSAdminAPI?: {
      tenants: {
        apiURL: string;
        apiPath: string;
      };
    };
  };
  kongRFIDAPI?: any;
}
